import { FunctionComponent, lazy, ReactNode, Suspense } from "react";
import { Navigate } from "react-router";

import { AppRole } from "../../../../@api";

import { RouteConstants } from "../../../models/enums";
import { useStore } from "../../../stores/StoreContext";

interface IRoleCheckGuardProps {
  children: ReactNode;
  role: AppRole;
}

export const RoleCheckGuard: FunctionComponent<IRoleCheckGuardProps> = ({ children, role }) => {
  const { permissionStore } = useStore();

  if (!permissionStore.userPermissions || !permissionStore.hasRole(role)) {
    return <Navigate to={`/${RouteConstants.LandingPage}`} replace />;
  }

  return children;
};

const LazyRoleCheckGuard = lazy(() =>
  import("./LazyRoleCheckGuard").then(module => ({
    default: module.RoleCheckGuard,
  })),
);

/**
 * Factory function that creates a lazy-loaded RoleCheckGuard with a specific role.
 * Use this in the guards array of lazyRouteWithGuards.
 *
 * @example
 * lazy: lazyRouteWithGuards({
 *   importer: () => import("./YourPage"),
 *   componentName: "YourPage",
 *   guards: [createLazyRoleCheckGuard(AppRole.SummarizationFeature)],
 * })
 */
export const createLazyRoleCheckGuard = (role: AppRole): FunctionComponent<{ children: ReactNode }> => {
  const Guard: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
    <Suspense fallback={null}>
      <LazyRoleCheckGuard role={role}>{children}</LazyRoleCheckGuard>
    </Suspense>
  );

  return Guard;
};
